import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { assign, createMachine, enqueueActions, fromCallback } from 'xstate';
import { CONFIG_ITEM_KEYS } from '../../prebuilt/config-items/config-item-keys';
import { buildChannelId } from '../agents/build-channel-id';
import { streaming } from '../streaming';

/**
 * This type models when there is a "generate prompt" being used.
 *
 * interrogates the prompt.
 */

/**
 * This type models when there is a non-generative preset prompt being used.
 * @example
 * Example of Improve writing:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItem,
 * }
 * ```
 * @example
 * Example of Brainstorm:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItem,
 *   userInput: 'Facts about cats',
 * }
 * ```
 */

/**
 * For empty selections, the start and end will be the same
 */

/**
 * This is a state machine for the AI Experience. Consider this the source
 * of truth for the AI modal the moment it appears to the moment it closes.
 *
 * The state is initialised in ExperienceApplicationStateWrapper via
 * useAIExperienceService().
 *
 * The state machine can be updated from React components via send() or
 * streaming() as the generated response is streamed back from endpoint.
 *
 * Use the XState extension to access a visual editor for the state machine.
 * When making changes to the state machine, please update the visualisation
 * and add a screenshot of the new layout in your PR.
 *
 * @see https://hello.atlassian.net/wiki/spaces/EDAI/pages/3222977194/HOWTO+-+Using+XState+state+machine+in+Editor+AI#How-do-I-update-the-%40xstate-layout-diagram-in-code%3F
 */
export function createAIExperienceMachine(options) {
  var machine = createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QEECSACAogDwA5gCcBLMAOwGMx0BZAQ3IAsjSwBiAV1wloBcrcA9rCI8iA0rADaABgC6iUIOGjxCkNkQBGAMwA2ABwA6abukB2TZv37tAVgBMmgCwAaEAE8t2w-oCcm+207bXtA-199AF9ItzQsPEISCio6RmYwQ1geWgI+CHQs3jYAayJyYvQBADMqmXkkECURMVI1DQRNM0cfWxsQ7U1bXVsBt08EEOljMzNbX10nW1mh+ydo2IwcfGIyShp6JhZM7NzIAuy+VlLyyprJTXrFIWbVBvbLeyn7Je1pc1NpJYxohJsZ9E5fNJwU5Zk5dNp1iA4ltErsUgd0oYALYCbgAG3Q9FEADcqIVLnByLR8HU1E0VK03oh7L4LIZnGYbPpHE4WfYzMCEL1fIZTCM5mYQty4YjkQkdsl9mkjjj8YTyCSyRc2FkBAQwABhcR8bA8ADKFyIWTKUjkdOeDLaIPsunshhCFjMul8TmkoRmgpdIrhei6phZcN8ss28qSe1ShwyqtoBKJRFJ5yKHC4RUJMFIPFtj0aDpaToQLMB7K9cOk2iccICge9hhD3u0kIc+jmaxiSJj2zj6OVSdxKfVmszfEMYFjaMMeIEtAgzCgU7Y5AEWNweLAfFpDXpZaZHV0XXdzgbZ6G4N82kFASh7pm-P0XQCQQRfblg7RSsT2JjqmGrplqRQznOyQLkuK6kGu5JsOwpDFKQAgAO6kOgVS0EQeIHk8yjHqA7SrMMhhepCThvjCLq2A+vy2D4DYRJ8VFeqs0bxL+ioJpiybAZOCEQdxlDQcuq7rqwsC0FiVBUowVDFGA7j4SWhGvMRXh2KKzjXiYHp6IKb6aIYthONokpnto3JhpxKIKvGGIqkBE6geuwmolBi7iXB7nebBUBSTw+oyapR4aeoWjgt4LoQqEQzer4-JGb4IpWAYUrUb6X4bFxnmOSOgFqmmGZCbOIkZP5ElCVVcHZtwfDoIwtCkCweKoBAYWlhF7zzCKUL6Jo5i2FYfqSoKVnkfW5hvt60hDDl-Z5Q5w4AfxrmldqHkrWJAV+TBq71bmRAQHQuBdepjKaRWYQ+AN9jgn6gKckZZhOMYDjSD6VhJSNi0-vlq18S5JVgdO5WA7t1VbbVgXUkQWE4XhdqHt1V2RRWQSMYNb6Qh2SV3g+UImboBiQgTrodnZkEFWtIMgZt4EQztsP7T5gUELmeJEFiIiQBdLzoyRTiaLohguvouiWBZwzsSlRhDPMnwmA2MK6NTFX-sDxUM2DGTM0OUO+TVB11TwAhKZh3O8zwiO4QLjonqEVYi-yb2SssvgPqRrZnpYPqct8ksa4DWvOTrglbQb86syb7OsMwuDsLb5sCAUDB6rb2H2yjBGC+Wnz+KKrrmZKA1wg+vJi+Tvgjd8IyDCHK1h6OEduWVNOVab8Ew93rBMLgtC0E1xpkFnSMO0RGNhGLb4mMTwwNvYD6aJChj+H4X0spTUbfgOoe8eH46g+50ded3bMBawyAAKoAAroMSYh4rwLToBAe5gBq-O52p+cnqvKid1vSdF5CGRYK9zyDG7INWw5hPhmCbkOFuRVj661Pp3I2PdwKw1YPqWAggJBUFTgUHmuEciTx6lFZw4teS-Asg4RYy8PBeBbPNKWiwQGb3sEgv8h9W5oMjkzTBsctrwztrufICFWDiP1MFFSv9wpC2ZJ9VsgcFidFASYImQRRSzF+tyEYoR-r72bvw1BAl25RxERfISnNGrWxEBtNg2AhK0CqHwAgAAKAASpgAAKj4gAmgAfWQAAGXCQAeQAOqYAACIAEpr6mOQeY9aJ8O4VSwe5exVBHG21BpQ5RFZXRTFdLCBYI0FjmCMqYdeZ4vrsVZBEXeuV7JpKcgIyxjNwY2PZrkrmZDbbMFBjI3ACM5EEAUcWJRBcvqMXmhZMwfoDDemYeMN6wYAgskls4Pwg1eE8S6RY5xGDsn6ifmANCklmCeIIAIKARRinlg5EYQEtd4Si1sL0UYLCOh1m8A4EIqVFhfX8Ec2m2tBFWOERcsAVybnSKQihdCmFs7I1mWjAucJ3oL1+SNPwugHwjCMIEfQX0IS9Elo4SFQMj49L1ttQ2lySBIu1Hgvc0z0C4AeduHgLynZUTdJ8VeKsqILIfG9MW3xpB0PMsMQY6s97LU6YVDJ6CsmQ1ZdcySkAnG8q3LgAVijsUnhGiZbsKyYSpTMr8L2-yEo+ApWeB6NEAi2DpSgjVQi+nwsRe5XlCK2VBRyLbfUVR0iyQLIK66Sq3T2tAaTOYHqHykzdCs1pP0-htKWh0vhJyfWwr9dq4Nuq7FgEjSwaNPBWCUmpGAWNGMlUimlJyeEb16yaAfMZd0DZrBK1CL6T1Kr83HPVfTX1+tME6vZeBCNUax6sCpMkAkC7q1jybe8H5jEQGsmcF9VZUrfjkW9MKeEvpRaINHZ3b1k7i3Tv9Wy3JlbF0FiOo1dgsBCDoETsnLdUUIhMTfBSn5MwDBprmOvP6z0paryiDezW6T729MfaWgNFaq1gBrR+qgX6f1-sKRAWoprLrzJFsXT5kohidDelKgwpkIwui9P7BD7Tb3Ibbqh5l85NxYixC1fIg9dw8EatI64xRqhVHQGhEQDAR78cEzylMe5GqEYAx0LGaiOwHLMKyCD-zErsh+R2GYNgQyaC9ZxmF3Gz6iT4wJ0gQmVOib1rhkeBZZwmqxWRk88IRX+09IS0w95-ktNFPNMBFL5gQjY3mjjhaUNMrsxkBzSnhOqbcyuygmL7S+bjQNRjnJ-C9F5KsImNDuykoiKNIIuaAZmMS1x5LmC0tOeUyJsTHLstgDwg8PL-8406EYj8nQo0-R3gcIGJpopHAfBFnoUIVmms2aZSuWAVICAQEMKM3WdbSC0AAEa7nflaTbnVSODYxiEb4PhHyuwbAlabrJZuWACAt10PDEMHxW4y9y63zuGDGeto7J2Ac5Auz5q77QBjzXZP6QY8xnBJQfHebwUIzyLA7A2AI172NId+2coS4OttA72-Wmkl3HbXQGLoq1OgIgGF9PoabLoLy7LMo4Osu8+yoQ-vABoDW1WJgG9T67Z4ejWr07XcyX0e0mXmHoH5qzaezGW4VQopwpHalF1PEiuzjABFMGUmwQxXD-IGCKWYD1TC+kcJKOLQuC0Tua+uXXVCEAi2xksOV0u7Vy4t9ZcWXbWIPSsDLdXdNXdapWu7kpIZJe+9tbLh14xeSMUlCLIaehggQu+41l3q3zmQ1EUUOPry9OMViklb4pNUrJUdToYPvpbUWUi5H6Ff2Y+G1L9OWG5enbDfZJjmw3yl6CkZ+RPTdZBq+ldNyDvDKifWOyb3jI4iMVnAQgP669cRVUR9FLQOoXxhDW5ORawXYatmRGov7py+4Ul9sVtPJ6ACnOJ39PC9d3vSShr2VifFkUUO8MUEuCwbsO-U5TJFfJ-AZOxIZG2X9A7XWT-EicMVsL6fZZwP6DZRAL0KYCEOKBYW3bsSzfPYXTvB-EtHaWdN3VGfLZtesDPX4VeMpYYaiElc8RYMbKwIOKuSAotWzGdMtOdacINRFVArwO8KaD5Ng3oOjf5ToepWYXSbsGpV1AQpLYvGgkQl9LDGtSQjoaLd0BYXGNQ2jc3U-L0MwcWf0O8CwZNPHeLAnQvLvGAnaTfCAQw1YaQyUayCEQYPTeEOiRQ8ybwbPD1fweBEdfHH7VwqgtDHaNrZzTrPWQwroGw34DKUmefIYAUQzUmZ1PoPwBwM8PTTQ6PLaEnLw+g6HZkWuIwH0PZXSHwoIHtRYUySWZYRYTsSUCoovYnM7CHHbZAzUQwqURZXkO8XHLeUWabVecWdZEYJKOEF0fotw8CaosnMY2osXGHP4auKiODVYiENo-5UVEUIMZHLIuEXoaIaIIAA */
    id: 'AI Experience Machine',
    initial: 'started state',
    types: {
      context: {},
      events: {}
    },
    states: {
      'started state': {
        on: {
          'kick off': [{
            target: 'modal active state.experience.loading state',
            guard: 'neither base generate nor custom prompt'
          }, '#AI Experience Machine.modal active state.experience.command palette state']
        }
      },
      'modal active state': {
        type: 'parallel',
        initial: 'experience',
        on: {
          escape: {
            target: '#AI Experience Machine.modal active state.discard.active'
          },
          storeContextStatistics: {
            actions: 'storeContextStatistics'
          }
        },
        states: {
          experience: {
            initial: 'command palette state',
            states: {
              'loading state': {
                on: {
                  complete: {
                    target: 'review state',
                    actions: ['pushToHistory', 'updateMarkdown', 'clearUserInput', 'updateRovoActions']
                  },
                  error: {
                    target: '#error state.active'
                  },
                  'same cache key': {
                    target: 'review state',
                    actions: ['restoreResponseFromHistory', 'clearUserInput']
                  }
                },
                states: {
                  loading: {
                    invoke: {
                      src: fromCallback(function (_ref) {
                        var context = _ref.input,
                          sendBack = _ref.sendBack;
                        streaming(context)(sendBack);
                      }),
                      input: function input(_ref2) {
                        var context = _ref2.context;
                        return context;
                      }
                    },
                    on: {
                      stream: {
                        target: 'loading',
                        actions: ['updateMarkdown', 'updateLoadingStatus']
                      },
                      'update idMap': {
                        target: 'loading',
                        actions: 'updateIdMap'
                      },
                      'update traceIds': {
                        target: 'loading',
                        actions: 'updateTraceIds'
                      },
                      error: {
                        target: '#error state.active'
                      },
                      'response too similar': {
                        target: '#AI Experience Machine.modal active state.experience.review state',
                        actions: ['pushToHistory', 'clearUserInput']
                      }
                    }
                  }
                },
                initial: 'loading',
                entry: ['setLatestPrompt', 'updateChannelId'],
                exit: 'clearLoadingStatus'
              },
              'review state': {
                on: {
                  interrogate: {
                    target: 'loading state',
                    actions: ['clearFromHistory', 'updatePromptContextBasedOnTrigger', 'interrogate']
                  },
                  refine: {
                    target: 'loading state',
                    actions: ['clearFromHistory', 'updatePromptContextBasedOnTrigger', 'incrementRefinementCount', 'refine']
                  },
                  error: {
                    target: '#error state.active'
                  },
                  'retry prompt': {
                    target: 'loading state',
                    actions: ['updatePromptContextBasedOnTrigger', 'updateUserInput', 'updateUserInputADF', 'clearMarkdown', 'incrementRetryPromptCount', 'clearFromHistory']
                  },
                  'edit prompt': {
                    target: 'command palette state',
                    actions: ['editPrompt', 'clearFromHistory']
                  },
                  'feedback submitted': {
                    target: 'review state',
                    actions: ['feedbackSubmitted']
                  }
                },
                states: {
                  preview: {
                    on: {
                      'start refinement': 'refinement'
                    }
                  },
                  refinement: {
                    on: {
                      'cancel refinement': {
                        target: '#AI Experience Machine.modal active state.discard.active',
                        actions: ['clearUserInput', 'clearFocus']
                      },
                      'update user input': {
                        actions: 'updateUserInput'
                      },
                      'update user input adf': {
                        actions: 'updateUserInputADF'
                      }
                    }
                  }
                },
                initial: 'preview'
              },
              'command palette state': {
                on: {
                  'kickoff with command palette input': {
                    target: 'loading state',
                    actions: ['updatePromptContextBasedOnTrigger', 'updateContext', 'resetRefinementCount']
                    // Ignored via go/ees005
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  },
                  'update context': {
                    target: 'command palette state',
                    actions: ['updateContext'],
                    description: "This must be called before 'cancel' transition to make sure the context is updated before the 'if command input is empty' guard is evaluated."
                  },
                  'toggle use page knowledge': {
                    target: 'command palette state',
                    actions: 'toggleUsePageKnowledge'
                  }
                },
                description: "This state creates a nested state machine that handles all the state of the command palette.\n\n  It needs to know the 'userInput', 'baseGenerate' and 'configItem' from the AI Experience State Machine.\n\n  Whenever we exit this state we need to update the AI Experience State Machine's context with the 'updateContext' action."
              },
              //To avoid having to handle errors in every state, all errors are handled here.
              // All error events are 'error' type with the errorInfo object containing further details.
              // The experience application will handle the error and display the appropriate error message
              //based on what has been stored in context on entry to the error state.
              'error state': {
                id: 'error state',
                initial: 'inactive',
                states: {
                  inactive: {},
                  active: {
                    entry: ['updateContext', 'storeFailureContext', 'clearLoadingStatus'],
                    exit: ['clearFailureContext'],
                    on: {
                      retry: {
                        target: '#AI Experience Machine.modal active state.experience.loading state.loading'
                      }
                    }
                  }
                }
              }
            }
          },
          discard: {
            initial: 'inactive',
            states: {
              inactive: {
                on: {
                  'enable discard': {
                    target: 'active',
                    actions: 'updateContext'
                  }
                }
              },
              active: {
                on: {
                  'disable discard': {
                    target: 'inactive',
                    actions: 'updateContext'
                  },
                  escape: 'inactive'
                }
              }
            }
          }
        }
      }
    },
    context: options.context,
    on: {
      'update positions': {
        actions: 'updatePositions'
      }
    }
  }, {
    actions: {
      updatePositions: assign(function (_ref3) {
        var event = _ref3.event;
        if (event.type !== 'update positions') {
          return {};
        }
        var isEmpty = event.positions[0] === event.positions[1];
        var selectionType = isEmpty ? 'empty' : 'range';
        return {
          positions: event.positions,
          isEmpty: isEmpty,
          selectionType: selectionType
        };
      }),
      storeFailureContext: assign(function (_ref4) {
        var event = _ref4.event;
        if (['error'].includes(event.type) && 'errorInfo' in event) {
          return {
            apiErrorInfo: event.errorInfo
          };
        }
        return {};
      }),
      clearFailureContext: assign(function () {
        return {
          apiErrorInfo: {}
        };
      }),
      updateMarkdown: assign(function (_ref5) {
        var event = _ref5.event;
        if (event.type !== 'stream' && event.type !== 'complete') {
          return {};
        }
        return {
          markdown: event.markdown,
          feedbackSubmitted: undefined
        };
      }),
      updateLoadingStatus: assign(function (_ref6) {
        var event = _ref6.event;
        if (event.type !== 'stream') {
          return {};
        }
        // If the loading status is empty, we want to clear it from the context
        if (event.loadingStatus === '') {
          return {
            loadingStatus: undefined
          };
        }
        return {
          loadingStatus: event.loadingStatus
        };
      }),
      clearLoadingStatus: assign(function () {
        return {
          loadingStatus: undefined
        };
      }),
      storeContextStatistics: assign(function (_ref7) {
        var event = _ref7.event;
        if (event.type === 'storeContextStatistics') {
          return {
            contextStatistics: event.contextStatistics
          };
        }
        return {};
      }),
      clearMarkdown: assign(function (_ref8) {
        var event = _ref8.event;
        if (event.type !== 'retry prompt') {
          return {};
        }
        return {
          markdown: undefined,
          idMap: undefined
        };
      }),
      interrogate: assign(function (_ref9) {
        var context = _ref9.context,
          event = _ref9.event;
        if (event.type !== 'interrogate') {
          return {};
        }
        /**
         * Need to wipe out markdown as per user story designs where we don't use
         * the prior response in the latest response
         */
        return {
          markdown: '',
          configItem: context.configItem.key !== CONFIG_ITEM_KEYS.ROVO_AGENT ? context.baseGenerate : context.configItem,
          userInput: event.userInput,
          userADFInput: event.userADFInput,
          latestPromptResponse: event.latestPromptResponse
        };
      }),
      refine: assign(function (_ref10) {
        var event = _ref10.event;
        if (event.type !== 'refine') {
          return {};
        }
        return {
          markdown: '',
          configItem: event.configItem,
          latestPromptResponse: event.latestPromptResponse
        };
      }),
      clearUserInput: assign(function () {
        return {
          userInput: '',
          userADFInput: undefined
        };
      }),
      updateUserInput: assign(function (_ref11) {
        var event = _ref11.event;
        if (event.type !== 'update user input' && event.type !== 'retry prompt') {
          return {};
        }
        return {
          userInput: event.input
        };
      }),
      updateUserInputADF: assign(function (_ref12) {
        var event = _ref12.event;
        if (event.type !== 'update user input adf' && event.type !== 'retry prompt') {
          return {};
        }
        return {
          userADFInput: event.adf
        };
      }),
      updateRovoActions: assign(function (_ref13) {
        var _event$rovoActions;
        var context = _ref13.context,
          event = _ref13.event;
        if (event.type !== 'complete') {
          return {};
        }

        // We don't want to update if there are no rovo actions or
        // an agent hasn't been set
        if (!((_event$rovoActions = event.rovoActions) !== null && _event$rovoActions !== void 0 && _event$rovoActions.length) || !context.configItem.agent) {
          return {
            rovoActions: {}
          };
        }
        return {
          rovoActions: event.rovoActions.reduce(function (prevValue, currentValue) {
            var key = currentValue.data.suggestion === 'insert' ? 'empty' : 'range';
            prevValue[key] = currentValue.data;
            return prevValue;
          }, {})
        };
      }),
      feedbackSubmitted: assign(function (_ref14) {
        var context = _ref14.context,
          event = _ref14.event;
        if (event.type !== 'feedback submitted') {
          return {};
        }
        return {
          feedbackSubmitted: event.sentiment
        };
      }),
      pushToHistory: assign(function (_ref15) {
        var context = _ref15.context,
          event = _ref15.event;
        if (event.type !== 'complete' && event.type !== 'response too similar') {
          return {};
        }
        var cacheKey = event.cacheKey;

        // Check if the most recent entry is the same as the new event to prevent duplicates
        var entries = context.responseHistory.entries;
        if (entries.length > 0 && entries[entries.length - 1].cacheKey === cacheKey) {
          return {};
        }
        var historyEvent;
        if (event.type === 'complete') {
          historyEvent = {
            userInput: context.userInput || '',
            userADFInput: context.userADFInput,
            configItem: context.configItem,
            markdown: event.markdown,
            cacheKey: cacheKey,
            modelInput: event.modelInput,
            inputOutputDiffRatio: event.inputOutputDiffRatio,
            // Ignored via go/ees005
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            latestPrompt: context.latestPrompt
          };
        }
        if (event.type === 'response too similar') {
          historyEvent = {
            userInput: context.userInput || '',
            userADFInput: context.userADFInput,
            configItem: context.configItem,
            markdown: '',
            responseTooSimilar: true,
            cacheKey: cacheKey,
            modelInput: event.modelInput,
            inputOutputDiffRatio: event.inputOutputDiffRatio,
            // Ignored via go/ees005
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            latestPrompt: context.latestPrompt
          };
        }
        if (!historyEvent) {
          return {};
        }

        // Calculate the size of the historyEvent object for analytics
        var historyEventSize = new Blob([JSON.stringify(historyEvent)]).size;
        var totalSize = context.responseHistory.totalSize + historyEventSize;

        // Add the new event to the end of the entries array
        var updatedEntries = [].concat(_toConsumableArray(entries), [historyEvent]);
        // Ensure the entries array does not exceed 5 items to limit memory usage
        if (updatedEntries.length > 5) {
          updatedEntries.shift();
        }
        return {
          responseHistory: {
            entries: updatedEntries,
            positionFromEnd: 0,
            // Reset the position
            totalSize: totalSize,
            traversals: context.responseHistory.traversals
          }
        };
      }),
      restoreResponseFromHistory: assign(function (_ref16) {
        var context = _ref16.context,
          event = _ref16.event;
        if (event.type !== 'same cache key') {
          return {};
        }
        var entries = context.responseHistory.entries;
        var historyEvent = entries[entries.length - 1];
        if (!historyEvent) {
          return {};
        }
        return {
          userInput: historyEvent.userInput,
          userADFInput: historyEvent.userADFInput,
          markdown: historyEvent.markdown,
          configItem: historyEvent.configItem,
          latestPromptResponse: historyEvent.markdown
        };
      }),
      clearFromHistory: assign(function (_ref17) {
        var event = _ref17.event;
        if (event.type !== 'retry prompt') {
          return {};
        }
        return {
          responseHistory: {
            entries: [],
            positionFromEnd: 0,
            totalSize: 0,
            traversals: 0
          }
        };
      }),
      setLatestPrompt: enqueueActions(function (_ref18) {
        var enqueue = _ref18.enqueue,
          check = _ref18.check;
        if (check({
          type: 'is base generate'
        })) {
          enqueue(assign(function (_ref19) {
            var context = _ref19.context;
            return {
              latestPrompt: {
                userInput: (context === null || context === void 0 ? void 0 : context.userInput) || '',
                userADFInput: context === null || context === void 0 ? void 0 : context.userADFInput
              }
            };
          }));
        } else if (check({
          type: 'has custom prompt'
        })) {
          enqueue(assign(function (_ref20) {
            var context = _ref20.context,
              event = _ref20.event;
            return {
              latestPrompt: _objectSpread(_objectSpread({}, event.type !== 'interrogate' && event.type !== 'refine' && {
                presetTitle: context.configItem
              }), {}, {
                userInput: (context === null || context === void 0 ? void 0 : context.userInput) || '',
                userADFInput: context === null || context === void 0 ? void 0 : context.userADFInput
              })
            };
          }));
        } else {
          /**
           * Falls through to options which do not take additional
           * user input and is not base generate, e.g.
           * - Improve writing
           * - Summarize
           * Upon interrogation, there may also be user input.
           * We preference showing user input over the preset title for
           * non-partial presets.
           */
          enqueue(assign(function (_ref21) {
            var context = _ref21.context;
            return context !== null && context !== void 0 && context.userInput ? {
              latestPrompt: {
                userInput: (context === null || context === void 0 ? void 0 : context.userInput) || '',
                userADFInput: context === null || context === void 0 ? void 0 : context.userADFInput
              }
            } : {
              latestPrompt: {
                presetTitle: context.configItem
              }
            };
          }));
        }
      }),
      clearFocus: function clearFocus() {
        var _document$activeEleme;
        (_document$activeEleme = document.activeElement) === null || _document$activeEleme === void 0 || _document$activeEleme.blur();
      },
      updateContext: assign(function (_ref22) {
        var event = _ref22.event;
        if (event.type === 'kickoff with command palette input' || event.type === 'update context' || event.type === 'enable discard') {
          return _objectSpread({}, event.context);
        }
        if (event.type === 'disable discard') {
          return {
            onceDiscard: undefined
          };
        }
        return {};
      }),
      updateChannelId: assign(function (_ref23) {
        var context = _ref23.context,
          event = _ref23.event;
        var aiProvider = context.aiProvider,
          configItem = context.configItem;
        if (!aiProvider.getChannelVariables || !configItem.agent) {
          return {
            channelId: undefined,
            previousAgentId: undefined
          };
        }

        // if agent did not change, we don't need to update channelId preserving the same conversation history
        // as soon as agent changes, we need to recreate channelId to start a new conversation
        if (configItem.agent.id === context.previousAgentId) {
          return {};
        }
        return {
          channelId: buildChannelId(),
          previousAgentId: configItem.agent.id
        };
      }),
      updateIdMap: assign(function (_ref24) {
        var context = _ref24.context,
          event = _ref24.event;
        if (event.type === 'update idMap') {
          return {
            idMap: _objectSpread({}, event.context.idMap)
          };
        }
        return {};
      }),
      updateTraceIds: assign(function (_ref25) {
        var event = _ref25.event;
        if (event.type === 'update traceIds') {
          return {
            traceIds: event.context.traceIds
          };
        }
        return {};
      }),
      editPrompt: assign(function (_ref26) {
        var _context$baseGenerate, _context$configItem$a;
        var context = _ref26.context,
          event = _ref26.event;
        if (event.type !== 'edit prompt') {
          return {};
        }

        // from 'neither base generate nor custom prompt'
        var isBaseGenerate = context.configItem.title === ((_context$baseGenerate = context.baseGenerate) === null || _context$baseGenerate === void 0 ? void 0 : _context$baseGenerate.title);
        var hasCustomPrompt = context.configItem.promptLabel !== undefined;
        // if it's a preset prompt which doesn't need user input (like `Summarize writing`)
        // - we want to reset configItem
        // - we want to reset the user input
        var isPresetPrompt = !isBaseGenerate && !hasCustomPrompt;
        var configItem = context.configItem;

        // if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
        // - we don't want to reset the configItem
        //   - (keep the configItem) so that we can restore the selected parent later in UserInputCommandPaletteWithLogic
        // - we want to reset the user input
        var isSubmenuSelected = Boolean(context.parentPresetPromptLabel);

        // when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
        // - we want to reset the configItem
        // - we don't want to reset the user input
        var isDefaultAgent = Boolean((_context$configItem$a = context.configItem.agent) === null || _context$configItem$a === void 0 ? void 0 : _context$configItem$a.isDefault);
        var resetConfigItem = (isPresetPrompt || isDefaultAgent) && !isSubmenuSelected;
        var resetUserInput = (isPresetPrompt || isSubmenuSelected) && !isDefaultAgent;
        if (resetConfigItem && context.baseGenerate) {
          // from dismissCustomPrompt
          configItem = context.baseGenerate;
        }

        // If we detect a preset prompt, clear everything
        // otherwise preserve what we can.
        return {
          markdown: undefined,
          userInput: resetUserInput ? undefined : event.userInput,
          userADFInput: resetUserInput ? undefined : event.userADFInput,
          presetTitle: resetUserInput ? undefined : event.presetTitle,
          configItem: configItem,
          latestPromptResponse: undefined,
          editCount: context.editCount + 1,
          idMap: undefined
        };
      }),
      incrementRetryPromptCount: assign(function (_ref27) {
        var context = _ref27.context;
        return {
          retryPromptCount: context.retryPromptCount + 1
        };
      }),
      incrementRefinementCount: assign(function (_ref28) {
        var context = _ref28.context;
        return {
          refinementCount: context.refinementCount + 1
        };
      }),
      resetRefinementCount: assign(function () {
        return {
          refinementCount: 0
        };
      }),
      updatePromptContextBasedOnTrigger: assign(function (_ref29) {
        var event = _ref29.event;
        if (event.type === 'interrogate') {
          return {
            promptTrigger: 'interrogate',
            latestPromptRetried: false
          };
        }
        if (event.type === 'refine') {
          return {
            promptTrigger: 'refinementButton',
            latestPromptRetried: false
          };
        }
        if (event.type === 'retry prompt') {
          return {
            latestPromptRetried: true
          };
        }
        if (event.type === 'kickoff with command palette input') {
          return {
            promptTrigger: 'generate',
            latestPromptRetried: false
          };
        }
        return {};
      }),
      toggleUsePageKnowledge: assign(function (_ref30) {
        var context = _ref30.context,
          event = _ref30.event;
        if (event.type !== 'toggle use page knowledge') {
          return {};
        }
        // This is set to true by default
        return {
          usePageKnowledge: !context.usePageKnowledge
        };
      })
    },
    guards: _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, 'has custom prompt', function has_custom_prompt(_ref31) {
      var _context$configItem$a2;
      var context = _ref31.context;
      // when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
      var isDefaultAgent = Boolean((_context$configItem$a2 = context.configItem.agent) === null || _context$configItem$a2 === void 0 ? void 0 : _context$configItem$a2.isDefault);
      if (isDefaultAgent) {
        return false;
      }
      return context.configItem.promptLabel !== undefined;
    }), 'is base generate', function is_base_generate(_ref32) {
      var _context$baseGenerate2;
      var context = _ref32.context;
      // Doing configItem direct comparision fails in tests where we mutate or create custom
      // items.
      return context.configItem.title === ((_context$baseGenerate2 = context.baseGenerate) === null || _context$baseGenerate2 === void 0 ? void 0 : _context$baseGenerate2.title);
    }), 'neither base generate nor custom prompt', function neither_base_generate_nor_custom_prompt(_ref33) {
      var _context$baseGenerate3;
      var context = _ref33.context;
      var isBaseGenerate = context.configItem.title === ((_context$baseGenerate3 = context.baseGenerate) === null || _context$baseGenerate3 === void 0 ? void 0 : _context$baseGenerate3.title);
      var hasCustomPrompt = context.configItem.promptLabel !== undefined;
      return !isBaseGenerate && !hasCustomPrompt;
    }), 'if command input is empty', function if_command_input_is_empty(_ref34) {
      var _context$userInput, _context$userInput$tr;
      var context = _ref34.context;
      return ((_context$userInput = context.userInput) === null || _context$userInput === void 0 || (_context$userInput$tr = _context$userInput.trim) === null || _context$userInput$tr === void 0 || (_context$userInput$tr = _context$userInput$tr.call(_context$userInput)) === null || _context$userInput$tr === void 0 ? void 0 : _context$userInput$tr.length) === 0;
    })
  });
  return machine;
}

// We will try to remove this type in separate PR

// Typed explicitly due to old xstate version not having
// type generation
// Whenever adding a new event to the machine -- you will need to update this union